import { MathUtils, Vector3 } from 'three';
import { Centerline } from './centerline';
import { buildRawRmfFrames, Frame, sampleSValues } from './frames';
import { GRAVITY_BANK_MIN_LEN_SQ, gravityBankDefinable, nodeTangent } from './orientation';
import { RollKey, rollKeysFromNodes, sortRollKeys } from './roll';

const _cross = new Vector3();
const _preferred = new Vector3();
const _ref = new Vector3();

/**
 * Signed bank of `camUp` about `forward`, relative to gravity-upright /
 * inverted preferred up (matches the chart roll channel).
 */
export function bankRelativeToRmf(forward: Vector3, camUp: Vector3, refUp: Vector3): number {
  // Project both ups into the plane ⊥ forward so atan2 is well-defined.
  const ref = refUp.clone().addScaledVector(forward, -refUp.dot(forward));
  const up = camUp.clone().addScaledVector(forward, -camUp.dot(forward));
  if (ref.lengthSq() < 1e-10 || up.lengthSq() < 1e-10) return 0;
  ref.normalize();
  up.normalize();
  const sin = forward.dot(_cross.crossVectors(ref, up));
  const cos = ref.dot(up);
  return MathUtils.radToDeg(Math.atan2(sin, cos));
}

/** Recover the pre-roll (gravity-upright / inverted) up: u0 = u cos θ − r sin θ. */
export function rmfUpFromFrame(frame: Frame, out: Vector3): Vector3 {
  const θ = MathUtils.degToRad(frame.rollDeg);
  const c = Math.cos(θ);
  const s = Math.sin(θ);
  return out.copy(frame.up).multiplyScalar(c).addScaledVector(frame.right, -s).normalize();
}

/**
 * World-up projected ⊥ tangent — the "rails level with the ground" reference.
 * Returns null when the tangent is too vertical for a stable preferred up.
 */
export function groundPreferredUp(tan: Vector3, out = new Vector3()): Vector3 | null {
  out.set(0, 1, 0).addScaledVector(tan, -tan.y);
  if (out.lengthSq() < GRAVITY_BANK_MIN_LEN_SQ) return null;
  return out.normalize();
}

/**
 * Signed bank (°) of `up` about `tan`, relative to rails-level-with-ground.
 * Null when the tangent is too vertical.
 */
export function bankVsGround(tan: Vector3, up: Vector3): number | null {
  const preferred = groundPreferredUp(tan, _preferred);
  if (!preferred) return null;
  return bankRelativeToRmf(tan, up, preferred);
}

/**
 * Signed bank (°) of `frame.up` about the tangent, relative to ground-upright.
 * Null when the tangent is too vertical.
 */
export function bankRelativeToGround(frame: Frame): number | null {
  return bankVsGround(frame.tan, frame.up);
}

/**
 * How far the pre-roll (track RMF) frame is from gravity-upright.
 * Add to authored relative roll so the UI reads 0° = rails level with the ground.
 * 0 when RMF is already gravity-upright; ~±180° when inverted. Near verticals
 * preferred-up collapses — return 0 (override is disabled).
 */
export function groundBankDisplayOffset(frame: Frame): number {
  rmfUpFromFrame(frame, _ref);
  _ref.addScaledVector(frame.tan, -_ref.dot(frame.tan));
  if (_ref.lengthSq() < 1e-10) return 0;
  _ref.normalize();
  const vsGround = bankVsGround(frame.tan, _ref);
  return vsGround ?? 0;
}

/** Track-relative authored degrees → ground-relative display degrees (overrides). */
export function displayBankDeg(frame: Frame, authoredDeg: number): number {
  return authoredDeg + groundBankDisplayOffset(frame);
}

/**
 * Ground-relative override degrees → track-relative authored roll.
 * `display 0` means rails level with the ground, converted into the RMF channel
 * that `buildFrames` applies.
 */
export function authoredBankFromDisplay(frame: Frame, displayDeg: number): number {
  return displayDeg - groundBankDisplayOffset(frame);
}

const RMF_SAMPLE_M = 0.5;

function nearestFrame(frames: Frame[], s: number): Frame {
  let best = frames[0]!;
  let bestD = Math.abs(best.s - s);
  for (let i = 1; i < frames.length; i++) {
    const f = frames[i]!;
    const d = Math.abs(f.s - s);
    if (d < bestD) {
      best = f;
      bestD = d;
    }
  }
  return best;
}

/**
 * Node `forcedBankDeg` is gravity-relative (0° = rails level with the ground).
 * Convert those pins into the track-relative RMF channel `buildFrames` applies,
 * using the current geometry's raw RMF so earlier-piece edits cannot drift the
 * authored gravity angle.
 */
export function authoredRollKeysFromTrack(centerline: Centerline): RollKey[] {
  const track = centerline.track;
  const L = centerline.totalLength;
  const tanAt = (i: number) =>
    L > 0 ? centerline.tanAt(centerline.spanStartS(i)) : nodeTangent(track.nodes[i]!);
  if (L <= 0) {
    return rollKeysFromNodes(track, (i) => centerline.spanStartS(i), tanAt);
  }
  const raw = buildRawRmfFrames(centerline, sampleSValues(centerline, RMF_SAMPLE_M));
  if (raw.length === 0) {
    return rollKeysFromNodes(track, (i) => centerline.spanStartS(i), tanAt);
  }
  const keys: RollKey[] = [];
  for (let i = 0; i < track.nodes.length; i++) {
    const gravityDeg = track.nodes[i]!.forcedBankDeg;
    if (gravityDeg === undefined) continue;
    if (!gravityBankDefinable(tanAt(i))) continue;
    const s = centerline.spanStartS(i);
    if (!isFinite(s)) continue;
    keys.push({
      s,
      deg: authoredBankFromDisplay(nearestFrame(raw, s), gravityDeg),
      interp: 'smooth',
    });
  }
  return sortRollKeys(keys);
}

/**
 * Rewrite `forcedBankDeg` on `nodeIndices` from track-relative (RMF) values to
 * gravity-relative, using the current centerline. Used when stamping templates
 * / fly-mode twist that are authored in the RMF channel.
 */
export function convertNodeAuthoredBanksToGravity(centerline: Centerline, nodeIndices: number[]): void {
  const L = centerline.totalLength;
  if (L <= 0 || nodeIndices.length === 0) return;
  const raw = buildRawRmfFrames(centerline, sampleSValues(centerline, RMF_SAMPLE_M));
  if (raw.length === 0) return;
  const nodes = centerline.track.nodes;
  for (const i of nodeIndices) {
    const node = nodes[i];
    if (!node || node.forcedBankDeg === undefined) continue;
    const s = centerline.spanStartS(i);
    if (!isFinite(s)) continue;
    if (!gravityBankDefinable(centerline.tanAt(s))) {
      delete node.forcedBankDeg;
      continue;
    }
    node.forcedBankDeg = Math.round(displayBankDeg(nearestFrame(raw, s), node.forcedBankDeg));
  }
}
